import React, { useState, useEffect, Suspense } from 'react';
import Hero from './components/Hero';
import ProjectPage from './components/ProjectPage';
import GradualBlur from './components/GradualBlur';
import ProjectToggleButton from './components/ProjectToggleButton';
import LeftSidebar from './components/LeftSidebar';
import RightSidebar from './components/RightSidebar';
import InfoView from './components/InfoView';
import NotFound404 from './components/NotFound404';
import CustomCursor from './components/CustomCursor';
import styles from './App.module.css';

// Lazy loading per componenti con TiltedCard (ottimizzazione INP)
const ProjectsSection = React.lazy(() => import('./components/ProjectsSection'));
const GridView = React.lazy(() => import('./components/GridView'));

// Componente di loading per lazy components
const LazyLoadingFallback: React.FC = () => (
  <div className={styles.lazyLoadingFallback}>
    <div className={styles.loadingSpinner}></div>
  </div>
);

interface Project {
  id: number;
  title: string;
  image: string;
  info: string;
}

const App: React.FC = () => {
  const [viewMode, setViewMode] = useState<'home' | 'grid' | 'info' | 'project' | '404'>('home');
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [selectedProject, setSelectedProject] = useState<Project | null>(null);

  const switchView = (mode: 'home' | 'grid' | 'info' | 'project' | '404', project?: Project | null) => {
    // Usa il progetto passato o lo stato corrente
    const projectToSave = project !== undefined ? project : selectedProject;
    
    // Salva lo stato nello storico del browser
    window.history.pushState(
      { viewMode: mode, selectedProject: projectToSave },
      '',
      window.location.href
    );
    
    // Aggiorna lo stato locale
    if (project !== undefined) {
      setSelectedProject(project);
    }
    
    setIsTransitioning(true);
    setTimeout(() => {
      setViewMode(mode);
      setIsTransitioning(false);
      // Scroll automatico in cima alla pagina
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }, 300);
  };

  const handleProjectClick = (project: Project) => {
    switchView('project', project);
  };

  // Gestione bottone indietro e URL non validi
  useEffect(() => {
    const handlePopState = (event: PopStateEvent) => {
      const path = window.location.pathname;
      
      // Se l'URL non è la root, mostra 404
      if (path !== '/') {
        setViewMode('404');
        setSelectedProject(null);
        return;
      }
      
      // Ripristina lo stato precedente dall'History API
      if (event.state) {
        const { viewMode: savedViewMode, selectedProject: savedProject } = event.state;
        // Aggiungi transizione per una navigazione fluida
        setIsTransitioning(true);
        setViewMode(savedViewMode);
        setSelectedProject(savedProject || null);
        setTimeout(() => {
          setIsTransitioning(false);
          window.scrollTo({ top: 0, behavior: 'smooth' });
        }, 300);
      } else {
        // Se non c'è stato (torna alla home), resetta
        setIsTransitioning(true);
        setViewMode('home');
        setSelectedProject(null);
        setTimeout(() => {
          setIsTransitioning(false);
          window.scrollTo({ top: 0, behavior: 'smooth' });
        }, 300);
      }
    };

    // Controlla l'URL iniziale
    const initialPath = window.location.pathname;
    if (initialPath !== '/') {
      setViewMode('404');
    }
    
    // Inizializza lo stato nello storico
    window.history.replaceState(
      { viewMode: 'home', selectedProject: null },
      '',
      window.location.href
    );

    // Ascolta i cambiamenti di URL (bottone indietro)
    window.addEventListener('popstate', handlePopState);
    
    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, []);

  // Titolo dinamico per SEO
  useEffect(() => {
    if (viewMode === 'project' && selectedProject) {
      document.title = `${selectedProject.title} - Alessia Mastrorilli`;
    } else if (viewMode === '404') {
      document.title = '404 - Pagina non trovata | Alessia Mastrorilli';
    } else {
      document.title = 'Alessia Mastrorilli - Art Director & Producer';
    }
  }, [viewMode, selectedProject]);

  const isHome = viewMode === 'home';
  const isProject = viewMode === 'project';

  return (
    <div className={styles.app}>
      <CustomCursor />
      <LeftSidebar onTitleClick={() => switchView('home')} forceFixedTop={!isHome || isProject} isProjectMode={isProject} />
      <RightSidebar
        onOverviewClick={() => switchView('grid')}
        onInfoClick={() => switchView('info')}
        forceFixedTop={!isHome || isProject}
        isProjectMode={isProject}
      />

      <div className={styles.contentContainer}>
        {viewMode === 'home' && (
          <div className={`${styles.homeContent} ${isTransitioning ? styles.transitioning : ''}`}>
            <Hero />
            <Suspense fallback={<LazyLoadingFallback />}>
              <ProjectsSection onProjectClick={handleProjectClick} />
            </Suspense>
          </div>
        )}
        {viewMode === 'project' && selectedProject && (
          <div className={`${styles.projectContent} ${isTransitioning ? styles.transitioning : ''}`} key={selectedProject.id}>
            <ProjectPage 
              projectId={selectedProject.id.toString()}
              projectName={selectedProject.title}
            />
          </div>
        )}
        {viewMode === 'grid' && (
          <div className={`${styles.gridContent} ${isTransitioning ? styles.transitioning : ''}`}>
            <Suspense fallback={<LazyLoadingFallback />}>
              <GridView onProjectClick={handleProjectClick} />
            </Suspense>
          </div>
        )}
        {viewMode === 'info' && (
          <div className={`${styles.gridContent} ${isTransitioning ? styles.transitioning : ''}`}>
            <InfoView />
          </div>
        )}
        {viewMode === '404' && (
          <NotFound404 onGoHome={() => switchView('home')} />
        )}
      </div>

      <GradualBlur
        target="page"
        position="bottom"
        height="8rem"
        strength={2}
        divCount={5}
        curve="bezier"
        exponential={true}
        opacity={1}
        style={{ bottom: 0, top: 'auto' }}
      />
      
      {viewMode === 'project' && selectedProject && (
        <ProjectToggleButton key={selectedProject.id} projectInfo={selectedProject.info} />
      )}
    </div>
  );
};

export default App;
